import { Alert, AlertIcon, Badge, Text } from "@chakra-ui/react";
import { FiPlus, FiTrash2 } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { CompactSelect, CompactTextInput } from "newui/components/ui/InputWithLabel";
import { cn } from "utils/helpers";
import { COLUMN_TYPE_OPTIONS } from "../SpreadsheetEditor/column-types";
import type {
	ImportedQuoteTable,
	ImportedTableSource,
} from "../../types/imported-quote-table.type";

export interface AiSpreadsheetImportPreviewProps {
	tables: ImportedQuoteTable[];
	activeTableIndex: number;
	onSelectTable: (index: number) => void;
	onTableNameChange: (name: string) => void;
	onRemoveTable: () => void;
	onColumnTypeChange: (columnSortOrder: number, columnType: string) => void;
	onColumnNameChange: (columnSortOrder: number, name: string) => void;
	onCellEdit: (rowSortOrder: number, columnSortOrder: number, value: string) => void;
	onAddRow: () => void;
	onRemoveRow: (rowSortOrder: number) => void;
}

const cellBorder = "border border-[#EAECF0]";

/** Server-authoritative source → chip label + Chakra colour scheme (AC5). */
const SOURCE_CHIP: Record<
	ImportedTableSource,
	{ colorScheme: string; label: string }
> = {
	deterministic: { label: "Deterministic", colorScheme: "green" },
	ai: { label: "AI-assisted", colorScheme: "purple" },
	manual: { label: "Needs setup", colorScheme: "orange" },
};

const COLUMN_TYPE_SELECT_OPTIONS = COLUMN_TYPE_OPTIONS.map((opt) => ({
	value: opt.value,
	label: opt.label,
}));

const PLEASE_CONFIRM_PLACEHOLDER = "Select type…";

/**
 * Shared, api-agnostic preview body: per-table tabs, source chip, per-sheet
 * warnings, editable table-name / column-type / cell grid, and add/remove row.
 * Fully controlled — it holds no state and calls the supplied `sort_order`-keyed
 * handlers. Consumed by the RFx modal (task 006) and reused by the template
 * wizard (task 007) and the Quote-settings library (task 010), each wiring it to
 * a different import api module.
 */
export const AiSpreadsheetImportPreview = ({
	tables,
	activeTableIndex,
	onSelectTable,
	onTableNameChange,
	onRemoveTable,
	onColumnTypeChange,
	onColumnNameChange,
	onCellEdit,
	onAddRow,
	onRemoveRow,
}: AiSpreadsheetImportPreviewProps) => {
	const activeTable = tables[activeTableIndex];
	if (!activeTable) {
		return null;
	}

	const chip = SOURCE_CHIP[activeTable.source];
	// Columns are rendered in server-provided sort order.
	const orderedColumns = [...activeTable.columns].sort(
		(a, b) => a.sort_order - b.sort_order
	);

	return (
		<div data-testid="ai-import-preview">
			<div className="mb-3 flex items-center justify-between">
				<Text className="text-sm font-medium text-gray-700">
					{tables.length} table{tables.length !== 1 ? "s" : ""} detected
				</Text>
			</div>

			{tables.length > 1 && (
				<div
					className="mb-3 flex gap-1 overflow-x-auto"
					data-testid="table-tabs"
				>
					{tables.map((table, idx) => (
						<button
							key={table.sheet_name || idx}
							type="button"
							className={cn(
								"rounded-t-md border border-b-0 px-3 py-1.5 text-xs font-medium",
								idx === activeTableIndex
									? "border-[#EAECF0] bg-white text-gray-900"
									: "border-transparent bg-gray-100 text-gray-500 hover:text-gray-700"
							)}
							onClick={() => onSelectTable(idx)}
							data-testid={`table-tab-${idx}`}
						>
							{table.name}
						</button>
					))}
				</div>
			)}

			<div className="mb-2 flex items-center gap-2">
				<CompactTextInput
					className="flex-1 font-medium"
					value={activeTable.name}
					onChange={(e) => onTableNameChange(e.target.value)}
					aria-label="Table name"
					data-testid="table-name-input"
				/>
				<Badge
					colorScheme={chip.colorScheme}
					data-testid="source-chip"
					className="whitespace-nowrap"
				>
					{chip.label}
				</Badge>
				{tables.length > 1 && (
					<button
						type="button"
						className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
						onClick={onRemoveTable}
						title="Remove table"
						aria-label={`Remove table ${activeTable.name}`}
						data-testid="remove-table-btn"
					>
						<FiTrash2 size={14} />
					</button>
				)}
			</div>

			{activeTable.warnings.length > 0 && (
				<Alert
					status="warning"
					className="mb-3 rounded-md"
					data-testid="table-warning"
				>
					<AlertIcon />
					<div className="text-sm">
						{activeTable.warnings.map((warning) => (
							<Text key={warning}>{warning}</Text>
						))}
					</div>
				</Alert>
			)}

			<div className="overflow-x-auto rounded-lg border border-[#EAECF0]">
				<table className="w-full table-auto" data-testid="ai-preview-table">
					<caption className="sr-only">Spreadsheet import preview</caption>
					<thead>
						<tr className="bg-blue-50">
							<th
								className={cn(
									cellBorder,
									"w-10 px-2 py-1 text-center text-xs font-medium text-gray-500"
								)}
							>
								#
							</th>
							{orderedColumns.map((col) => {
								const unresolved = col.column_type === null;
								return (
									<th
										key={col.sort_order}
										className={cn(cellBorder, "px-2 py-1")}
										style={{ minWidth: "140px" }}
									>
										<CompactSelect
											value={col.column_type ?? ""}
											hasError={unresolved}
											placeholder={
												unresolved
													? PLEASE_CONFIRM_PLACEHOLDER
													: undefined
											}
											options={COLUMN_TYPE_SELECT_OPTIONS}
											onChange={(e) =>
												onColumnTypeChange(
													col.sort_order,
													e.target.value
												)
											}
											aria-label={`Column type for ${col.name || `column ${col.sort_order + 1}`}`}
											data-testid={`ai-col-type-${col.sort_order}`}
										/>
										{unresolved && (
											<Text
												className="mt-0.5 text-[10px] font-medium text-orange-500"
												data-testid={`ai-col-confirm-${col.sort_order}`}
											>
												Please confirm
											</Text>
										)}
									</th>
								);
							})}
							<th className="w-10 bg-blue-50" />
						</tr>
						<tr className="bg-[#F9FAFB]">
							<th className={cn(cellBorder, "w-10 px-2 py-1")} />
							{orderedColumns.map((col) => (
								<th
									key={col.sort_order}
									className={cn(cellBorder, "px-2 py-1")}
								>
									<CompactTextInput
										className="font-medium"
										value={col.name}
										onChange={(e) =>
											onColumnNameChange(
												col.sort_order,
												e.target.value
											)
										}
										aria-label={`Column label for column ${col.sort_order + 1}`}
										data-testid={`ai-col-label-${col.sort_order}`}
									/>
								</th>
							))}
							<th className="w-10 bg-[#F9FAFB]" />
						</tr>
					</thead>
					<tbody>
						{activeTable.rows.map((row, rIdx) => (
							<tr key={row.sort_order}>
								<td
									className={cn(
										cellBorder,
										"w-10 bg-gray-50 text-center text-xs text-gray-500"
									)}
								>
									{rIdx + 1}
								</td>
								{orderedColumns.map((col) => {
									const cellValue = row.cells[col.sort_order];
									return (
										<td
											key={col.sort_order}
											className={cn(cellBorder, "px-1 py-1")}
										>
											<CompactTextInput
												className="border-transparent bg-transparent"
												value={cellValue ?? ""}
												onChange={(e) =>
													onCellEdit(
														row.sort_order,
														col.sort_order,
														e.target.value
													)
												}
												data-testid={`ai-cell-${row.sort_order}-${col.sort_order}`}
											/>
										</td>
									);
								})}
								<td className="px-1">
									<button
										type="button"
										className="rounded p-0.5 text-red-400 hover:text-red-600"
										onClick={() => onRemoveRow(row.sort_order)}
										title="Remove row"
										aria-label={`Remove row ${rIdx + 1}`}
										data-testid={`ai-remove-row-${row.sort_order}`}
									>
										<FiTrash2 size={10} />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="mt-2">
				<Button
					size="xs"
					btntype="secondary"
					onClick={onAddRow}
					data-testid="ai-add-row-btn"
				>
					<FiPlus className="mr-1" />
					Add Row
				</Button>
			</div>
		</div>
	);
};
